/* eslint-disable @angular-eslint/prefer-standalone */
import { AfterViewInit, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Permissions } from '@app/core/guard/Permissions';
import { UserRegisterRequest } from '@app/core/models/auth/user-register-request';
import { ErrorResponse } from '@app/core/models/commom/error-response';
import { UpdateUserNotificationPreference } from '@app/core/models/users/user-notification-preference.model';
import { VaultBaseComponent } from '@app/shared/components/base/vault-base.component';
import { Checkbox } from '@interfaces/a-organizar/checkbox';
import { PermissionGroup } from '@interfaces/a-organizar/permissionGroup';
import { User } from '@interfaces/a-organizar/user';
import { ApiResponse } from '@interfaces/common/apiResponse';
import { Option } from '@interfaces/common/option';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { FormErrorHandlerService } from '@services/helpers/form-error-handler.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { UtilsService } from '@services/helpers/utils.service';
import { CompanyService } from '@services/http/company.service';
import { EconomicGroupsHttpService } from '@services/http/economic-groups/economic-groups-http.service';
import { PartnersHttpService } from '@services/http/partners/partners-http.service';
import { UserService } from '@services/http/user.service';
import { UserNotificationPreferenceHttpService } from '@services/http/users/user-notification-preference-http.service';
import { NotificationPreferencesFormComponent } from '@shared/components/notification-preferences-form/notification-preferences-form.component';
import { UserAccessPanelComponent } from '@app/features/dashboard/economic-groups/components/user-access-panel/user-access-panel.component';
import { nameof } from '@src/app/core/utils/nameof.utils';
import { AppState } from '@src/app/state';
import { selectTenantId } from '@src/app/state/auth/auth.selectors';
import { MenuItem } from 'primeng/api';
import { Observable, catchError, of, switchMap } from 'rxjs';
import { describeBackendError } from '@src/app/core/utils/backend-error.utils';
import { UserEmailSignatureFormComponent } from './components/user-email-signature-form/user-email-signature-form.component';
import { selectedGroupsGrantMarketMonitorView } from './user-register-market-section.helper';

@Component({
  templateUrl: './user-register.component.html',
  styleUrls: ['./user-register.component.scss'],
  standalone: false,
})
export class UserRegisterComponent extends VaultBaseComponent implements OnInit, AfterViewInit {
  @ViewChild('notificationPreferencesForm')
  notificationPreferencesForm?: NotificationPreferencesFormComponent;

  /**
   * Painel de escopo de empresas. Na criação ele roda como seletor puro e é este
   * componente que persiste a escolha depois que o usuário passa a existir.
   */
  @ViewChild('accessPanel')
  accessPanel?: UserAccessPanelComponent;

  /** Cargo, área e selos da assinatura de e-mail (card 3456). */
  @ViewChild('emailSignatureForm')
  emailSignatureForm?: UserEmailSignatureFormComponent;

  private _permissionsCheckboxes: Checkbox[] | null = null;
  private _permissionsAdminCheckboxes: Checkbox[] | null = null;
  private _permissionsTenantCheckboxes: Checkbox[] | null = null;
  private pendingNotificationPreferences: unknown = null;
  userPermissions = Permissions.User;
  userId: string = '';
  breadcrumbs: MenuItem[] = [];
  home: MenuItem = {
    icon: 'pi pi-home',
    routerLink: '/dashboard/dashboard-management',
    label: this.t(this.tk.General.Dashboard),
  };
  userStatusOptions: Option[] = [
    { label: this.t(this.tk.User.Active), value: '1' },
    { label: this.t(this.tk.User.Inactive), value: '0' },
  ];
  selectedStatus: Option | null = {
    label: this.t(this.tk.User.Active),
    value: '1',
  };
  userTypeOptions: Option[] = [];
  selectedUserType: Option | null = null;
  userLanguagesOptions: Option[] = [];
  selectedLanguage: Option | null = null;
  userDateFormatsOptions: Option[] = [];
  selectedDateFormat: Option | null = null;
  userTimeFormatsOptions: Option[] = [];
  selectedTimeFormat: Option | null = null;
  userTimeZoneOptions: Option[] = [];
  selectedTimeZone: Option | null = null;
  tenantOptions: Option[] = [];
  selectedTenant: Option | null = null;

  /**
   * Parceiros comerciais que podem ser escolhidos no tipo PARTNER.
   *
   * Carregado sob demanda (ver {@link loadPartnerOptions}), e não no `ngOnInit`: o endpoint
   * de parceiros exige a permissão de Gerenciamento › Parceiros, então carregar sempre faria
   * a tela mostrar um toast de erro para quem cadastra usuário comum e nunca chega no tipo
   * PARTNER.
   */
  partnerOptions: Option[] = [];
  private partnerOptionsRequested = false;
  defaultLanguage: string = 'pt-BR';
  permissionsGroups: PermissionGroup[] = [];
  permissionsGroupsAdminTenant: {
    tenant: PermissionGroup[] | null;
    admin: PermissionGroup[] | null;
  } = {
    tenant: null,
    admin: null,
  };
  checkAllPermissions: Checkbox = {
    label: this.t(this.tk.User.MarkAll),
    name: 'mark-all',
    value: 'mark-all',
    isDisabled: false,
    isChecked: false,
  };

  sendOnboardingEmailCheckbox: Checkbox = {
    label: 'Enviar email de onboarding ',
    name: 'mark-all',
    value: 'true',
    isDisabled: false,
    isChecked: false,
  };

  checkAllAdminPermissions: Checkbox = {
    label: this.t(this.tk.User.MarkAll),
    name: 'mark-all',
    value: 'mark-all',
    isDisabled: false,
    isChecked: false,
  };

  checkAllTenantPermissions: Checkbox = {
    label: this.t(this.tk.User.MarkAll),
    name: 'mark-all',
    value: 'mark-all',
    isDisabled: false,
    isChecked: false,
  };

  companiesOptions: Option[] = [];
  selectedCompany: Option | null = null;

  public userRegisterProps = nameof<UserRegisterRequest>();
  isPublicGlobalBackofficeUserCheckbox: Checkbox = {
    label: 'Usuário público do backoffice global',
    name: 'isPublicGlobalBackofficeUser',
    value: 'true',
    isDisabled: false,
    isChecked: false,
  };

  userRegisterForm: FormGroup = this.formBuilder.group({
    [this.userRegisterProps.search]: [''],
    [this.userRegisterProps.status]: ['1'],
    [this.userRegisterProps.firstName]: ['', [Validators.required]],
    [this.userRegisterProps.surName]: ['', [Validators.required]],
    [this.userRegisterProps.email]: ['', [Validators.required, Validators.email]],
    [this.userRegisterProps.companyId]: [''],
    [this.userRegisterProps.phoneNumber]: ['55'],
    [this.userRegisterProps.roles]: ['', Validators.required],
    [this.userRegisterProps.tenantId]: [''],
    [this.userRegisterProps.partnerId]: [''],
    [this.userRegisterProps.permissionsGroupsIds]: this.formBuilder.array(
      [],
      [Validators.required, Validators.min(1)]
    ),
    [this.userRegisterProps.timeZoneInfoId]: ['', [Validators.required]],
    [this.userRegisterProps.dateFormatDefinitionId]: ['', [Validators.required]],
    [this.userRegisterProps.timeFormatDefinitionId]: ['', [Validators.required]],
    [this.userRegisterProps.languageId]: ['', [Validators.required]],
    [this.userRegisterProps.isPublicGlobalBackofficeUser]: [false],
  });
  search: { tenant: string; admin: string; default: string } = {
    tenant: '',
    admin: '',
    default: '',
  };

  tenantId?: string;

  constructor(
    translateService: TranslateService,
    private utilsService: UtilsService,
    private formBuilder: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private userService: UserService,
    private formErrorHandler: FormErrorHandlerService,
    private cdr: ChangeDetectorRef,
    private toast: ToastHelperService,
    private companyService: CompanyService,
    private notificationPreferenceService: UserNotificationPreferenceHttpService,
    private economicGroupsService: EconomicGroupsHttpService,
    private partnersService: PartnersHttpService,
    store: Store<AppState>
  ) {
    super(translateService);
    this.defaultLanguage = translateService.currentLang;

    store.select(selectTenantId).subscribe(tenantId => (this.tenantId = tenantId as string));
  }
  ngOnInit(): void {
    this.breadcrumbs = [
      { label: this.t(this.tk.General.Administration), routerLink: '/' },
      {
        label: this.t(this.tk.General.Users),
        routerLink: '/dashboard/user/list',
      },
      {
        label: this.t(this.tk.General.Register),
        routerLink: '/dashboard/user/register',
      },
    ];
    this.getVerifyUserId();

    this.getTenantOptions();
    this.getUserLanguagesOptions();
    this.getUserDateFormats();
    this.getUserTimeFormats();
    this.getUserTimezones();

    this.getUserTypesOptions();

    this.route.params.subscribe(params => {
      const companyId = params['companyId'];
      this.getCompanyOptions(companyId);
    });
  }

  ngAfterViewInit(): void {
    if (this.pendingNotificationPreferences) {
      this.notificationPreferencesForm?.applyNotificationPreferences(
        this.pendingNotificationPreferences
      );
      this.pendingNotificationPreferences = null;
    }
  }

  user: User | null = null;
  getVerifyUserId(): void {
    this.route.params.subscribe(params => {
      if (params['id']) {
        this.userId = params['id'];
        this.utilsService.setIsLoading(true);
        this.userService.getUserById(this.userId).subscribe({
          next: response => {
            this.user = response;
            this.utilsService.setIsLoading(false);
            if (response) {
              const user = response;

              // Set basic form fields...
              this.userRegisterForm.patchValue({
                firstName: user.firstName,
                surName: user.surName,
                email: user.email,
                phoneNumber: user?.phoneNumber || '55',
                languageId: user.languageId,
                dateFormatDefinitionId: user.dateFormatDefinitionId,
                timeFormatDefinitionId: user.timeFormatDefinitionId,
                timeZoneInfoId: user.timeZoneInfoId,
                roles: user.roles.map(role => role.id),
                isPublicGlobalBackofficeUser: user.isPublicGlobalBackofficeUser ?? false,
              });

              // Handle tenant selection if exists
              if (user.tenantId) {
                this.selectedTenant = this.tenantOptions.find(
                  tenant => tenant.value === user.tenantId
                ) as Option;
                this.userRegisterForm.patchValue({
                  tenantId: user.tenantId,
                });
              }

              // Handle company selection
              this.selectedCompany = this.companiesOptions.find(
                company => company.value === user.companyId
              ) as Option;
              if (this.selectedCompany) {
                this.userRegisterForm.patchValue({
                  companyId: user.companyId,
                });
              }
              this.resetPermissionGroupFormArray();
              // Handle user type and roles
              if (user.roles.length > 1) {
                this.selectedUserType = this.userTypeOptions.find(
                  role => role.label === 'TENANT + ADMIN'
                ) as Option;

                this.handleMultiRolesRequest(user.roles.map(role => role.id));
              } else {
                this.selectedUserType = this.userTypeOptions.find(
                  role => role.value === user.roles[0].id
                ) as Option;
                this.getUserPermissionsGroups(user.roles[0].id);
              }

              // Clear and repopulate permissions groups
              this.resetPermissionGroupFormArray();
              const permissionsGroups = user?.permissionsGroups || [];

              // Add each permission group ID to the form array
              permissionsGroups.forEach(permissionGroup => {
                this.permissionGroupFormArray.push(this.formBuilder.control(permissionGroup.id));
              });

              // Force form array validation update
              this.permissionGroupFormArray.updateValueAndValidity();

              // Update checkboxes state based on selected permissions
              this.reset_permissionsCheckboxes('all');
              this.updateCheckboxesState(permissionsGroups);

              // Atualiza o estado do checkbox visual
              this.isPublicGlobalBackofficeUserCheckbox.isChecked =
                !!user.isPublicGlobalBackofficeUser;

              // Carregar preferências de notificação (usuário parceiro não tem o card)
              if (!this.isPartnerUserType) {
                this.loadUserNotificationPreferences(this.userId);
              }
              this.emailSignatureForm?.load(this.userId);
            }
          },
          error: error => {
            this.utilsService.setIsLoading(false);
            console.error('getVerifyUserId error: ', error);
            this.toast.showToastError(
              this.t(this.tk.User.UserErrorLoadingUser),
              error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
            );
          },
        });
      } else {
        this.applyNotificationPreferencesToForm({});
      }

      if (params['companyId']) {
        const companyId = params['companyId'];
        this.selectedCompany = this.companiesOptions.find(
          company => company.value === companyId
        ) as Option;
        this.userRegisterForm?.get(this.userRegisterProps.companyId)?.setValue(companyId);
      }
    });
  }

  private updateCheckboxesState(permissionsGroups: PermissionGroup[]): void {
    const selectedIds = permissionsGroups.map(pg => pg.id);

    // Update default checkboxes
    if (this._permissionsCheckboxes) {
      this._permissionsCheckboxes.forEach(checkbox => {
        checkbox.isChecked = selectedIds.includes(checkbox.value);
      });
    }

    // Update admin checkboxes
    if (this._permissionsAdminCheckboxes) {
      this._permissionsAdminCheckboxes.forEach(checkbox => {
        checkbox.isChecked = selectedIds.includes(checkbox.value);
      });
    }

    // Update tenant checkboxes
    if (this._permissionsTenantCheckboxes) {
      this._permissionsTenantCheckboxes.forEach(checkbox => {
        checkbox.isChecked = selectedIds.includes(checkbox.value);
      });
    }
  }

  getCompanyOptions(companyId?: string) {
    this.companyService.getAllCompanys(this.selectedTenant?.value).subscribe({
      next: response => {
        this.companiesOptions =
          response?.data.map(
            company =>
              ({
                label: company.displayName,
                value: company.id,
              }) as Option
          ) || [];

        if (companyId) {
          this.selectedCompany =
            this.companiesOptions.find(option => option.value === companyId) || null;
        }
      },
      error: error => {
        console.error(error);
      },
    });
  }

  getTenantOptions(): void {
    this.utilsService.setIsLoading(true);
    this.userService.getUserTenants().subscribe({
      next: response => {
        this.utilsService.setIsLoading(false);
        if (response) {
          this.tenantOptions = (response.data || []).map(
            tenant =>
              ({
                label: tenant.name,
                value: tenant.id,
              }) as Option
          );

          this.selectedTenant = this.tenantOptions.find(
            tenant => tenant.value === this.tenantId
          ) as Option;
          this.userRegisterForm.controls[this.userRegisterProps.tenantId].setValue(this.tenantId);
        }
      },
      error: error => {
        this.utilsService.setIsLoading(false);
        console.log('getTenantOptions error: ', error);
        this.toast.showToastError(
          this.t(this.tk.Management.ErrorLoadingTenants),
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
    });
  }

  getUserTypesOptions(): void {
    this.utilsService.setIsLoading(true);
    this.userService.getUserRoles().subscribe({
      next: response => {
        this.utilsService.setIsLoading(false);
        const companyLabels = ['company', 'empresa'];
        if (response) {
          this.userTypeOptions = response.map(
            role =>
              ({
                label: role.name,
                value: role.id,
              }) as Option
          );

          if (!this.userId) {
            this.selectedUserType = this.userTypeOptions.find(role =>
              companyLabels.includes((role.label || '').toLowerCase())
            ) as Option;
          }
          this.userRegisterForm?.get(this.userRegisterProps.companyId)?.setValidators([]);
          this.userRegisterForm?.get(this.userRegisterProps.companyId)?.updateValueAndValidity();
          this.fillTenantAdminRole();
          this.handleUserTypeChange(this.selectedUserType);
        }
      },
      error: error => {
        this.utilsService.setIsLoading(false);
        console.log('getUserTypesOptions error: ', error);
        this.toast.showToastError(
          this.t(this.tk.User.UserErrorLoadingRoles),
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
    });
  }

  get userTypeIds(): string[] {
    return (this.selectedUserType?.value || '').split('@');
  }

  fillTenantAdminRole(): void {
    const tenantAdmin = { label: 'TENANT + ADMIN', value: '' };
    this.userTypeOptions
      .filter(role => role.label === 'TENANT' || role.label === 'ADMIN')
      .forEach(
        (role: Option, iRole: number) =>
          (tenantAdmin.value += `${iRole !== 0 ? '@' : ''}${role.value}`)
      );
    this.userTypeOptions.push(tenantAdmin);
  }

  getUserLanguagesOptions(): void {
    this.utilsService.setIsLoading(true);
    this.userService.getUserLanguages().subscribe({
      next: response => {
        this.utilsService.setIsLoading(false);
        if (response) {
          this.userLanguagesOptions = response.map(
            language =>
              ({
                label: language.code,
                value: language.id,
              }) as Option
          );
          const defaultLanguage = this.userLanguagesOptions.find(
            language => language.label === 'pt-BR'
          ) as Option;
          this.selectedLanguage = defaultLanguage;

          this.userRegisterForm.controls[this.userRegisterProps.languageId].setValue(
            this.selectedLanguage?.value
          );
          this.cdr.detectChanges();
        }
      },
      error: error => {
        this.utilsService.setIsLoading(false);
        console.log('getUserLanguagesOptions error: ', error);
        this.toast.showToastError(
          this.t(this.tk.User.UserErrorLoadingLanguages),
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
    });
  }

  getUserDateFormats(): void {
    this.utilsService.setIsLoading(true);
    this.userService.getUserDateFormats().subscribe({
      next: response => {
        this.utilsService.setIsLoading(false);
        if (response) {
          this.userDateFormatsOptions = response.map(
            dateFormat =>
              ({
                label: dateFormat.description,
                value: dateFormat.id,
              }) as Option
          );
          this.selectedDateFormat = this.userDateFormatsOptions.find(
            dateFormat => dateFormat.label === '14/04/2023'
          ) as Option;

          this.userRegisterForm.controls[this.userRegisterProps.dateFormatDefinitionId].setValue(
            this.selectedDateFormat?.value
          );
        }
      },
      error: error => {
        this.utilsService.setIsLoading(false);
        console.log('getUserDateFormats error: ', error);
        this.toast.showToastError(
          this.t(this.tk.User.UserErrorLoadingDateFormats),
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
    });
  }

  getUserTimeFormats(): void {
    this.utilsService.setIsLoading(true);
    this.userService.getUserTimeFormats().subscribe({
      next: response => {
        this.utilsService.setIsLoading(false);
        if (response) {
          this.userTimeFormatsOptions = response.map(
            timeFormat =>
              ({
                label: timeFormat.description,
                value: timeFormat.id,
              }) as Option
          );

          this.selectedTimeFormat = this.userTimeFormatsOptions.find(
            timeFormat => timeFormat.label === '13:45:30'
          ) as Option;

          this.userRegisterForm.controls[this.userRegisterProps.timeFormatDefinitionId].setValue(
            this.selectedTimeFormat?.value
          );
        }
      },
      error: error => {
        this.utilsService.setIsLoading(false);
        console.log('getUserTimeFormats error: ', error);
        this.toast.showToastError(
          this.t(this.tk.User.UserErrorLoadingTimeFormats),
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
    });
  }

  getUserTimezones(): void {
    this.utilsService.setIsLoading(true);
    this.userService.getUserTimeZones().subscribe({
      next: response => {
        this.utilsService.setIsLoading(false);
        if (response) {
          this.userTimeZoneOptions = response.map(
            timezone =>
              ({
                label: `${timezone.name} - ${timezone.utcOffset}`,
                value: timezone.id,
              }) as Option
          );
          this.selectedTimeZone = this.userTimeZoneOptions.find(
            timezone => timezone.label === 'Brasilia - UTC-03:00'
          ) as Option;

          this.userRegisterForm.controls[this.userRegisterProps.timeZoneInfoId].setValue(
            this.selectedTimeZone?.value
          );
        }
      },
      error: error => {
        this.utilsService.setIsLoading(false);
        console.log('getUserTimezones error: ', error);
        this.toast.showToastError(
          this.t(this.tk.User.UserErrorLoadingTimezones),
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
    });
  }

  cancelRegister(): void {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      this.router.navigate(['dashboard/user/list']);
    }
  }

  handleOptionChange(selectedOptions: Option | null, key: string): void {
    switch (key) {
      case 'selectedLanguage':
        this.selectedLanguage = selectedOptions;
        this.userRegisterForm.controls[this.userRegisterProps.languageId].setValue(
          selectedOptions?.value
        );
        break;
      case 'selectedUserType':
        this.selectedUserType = selectedOptions;
        this.sendOnboardingEmailCheckbox.isChecked = false;
        this.handleUserTypeChange(selectedOptions);
        break;
      case 'selectedTenant':
        this.selectedTenant = selectedOptions;
        this.userRegisterForm.controls[this.userRegisterProps.tenantId].setValue(
          selectedOptions?.value
        );
        this.getCompanyOptions();
        break;
      case 'selectedDateFormat':
        this.selectedDateFormat = selectedOptions;
        this.userRegisterForm.controls[this.userRegisterProps.dateFormatDefinitionId].setValue(
          selectedOptions?.value
        );
        break;
      case 'selectedTimeFormat':
        this.selectedTimeFormat = selectedOptions;
        this.userRegisterForm.controls[this.userRegisterProps.timeFormatDefinitionId].setValue(
          selectedOptions?.value
        );
        break;
      case 'selectedTimeZone':
        this.selectedTimeZone = selectedOptions;
        this.userRegisterForm.controls[this.userRegisterProps.timeZoneInfoId].setValue(
          selectedOptions?.value
        );
        break;
      case 'selectedStatus':
        this.selectedStatus = selectedOptions;
        this.userRegisterForm.controls[this.userRegisterProps.status].setValue(
          selectedOptions?.value
        );
        break;
      case 'selectedCompany':
        this.selectedCompany = selectedOptions;
        this.userRegisterForm.controls[this.userRegisterProps.companyId].setValue(
          selectedOptions?.value
        );
        break;
    }
  }

  handleUserTypeChange(selectedOptions: Option | null): void {
    let roles = [];
    this.verifyTenantValidators();
    this.verifyPartnerValidators();
    roles = (selectedOptions?.value || '').split('@');
    this.userRegisterForm.controls[this.userRegisterProps.roles].setValue(roles);
    if (selectedOptions?.value) {
      const roleIds = selectedOptions?.value.split('@');
      if (selectedOptions?.label !== 'TENANT + ADMIN') {
        this.getUserPermissionsGroups(roleIds[0]);
      } else {
        this.handleMultiRolesRequest(roleIds);
      }
    }
  }

  handleMultiRolesRequest(roleIds: string[] = []): void {
    this.resetPermissionGroupFormArray();
    roleIds.forEach((roleId: string, index: number) => {
      const key = index === 0 ? 'tenant' : 'admin';
      this.utilsService.setIsLoading(true);
      this.getUserPermissionsFromService(roleId, key).subscribe({
        next: res => {
          this.utilsService.setIsLoading(false);
          if (res?.data) {
            this.permissionsGroupsAdminTenant[`${key}`] = res?.data;
            this.reset_permissionsCheckboxes(key);
            this.cdr.detectChanges();
          }
        },
        error: err => {
          console.log(`getUserPermissionsFromService ${index}: `, err);
          this.utilsService.setIsLoading(false);

          this.toast.showToastSuccess(this.t(this.tk.User.UserErrorLoadingPermissions), '');
        },
      });
    });
  }

  verifyTenantValidators(): void {
    if (this.shouldShowTenantOptions) {
      this.userRegisterForm.controls[this.userRegisterProps.tenantId].setValidators([
        Validators.required,
      ]);
    } else {
      this.userRegisterForm.controls[this.userRegisterProps.tenantId].setValue('');
      this.selectedTenant = null;
      this.userRegisterForm.controls[this.userRegisterProps.tenantId].clearValidators();
    }
    this.userRegisterForm.controls[this.userRegisterProps.tenantId].updateValueAndValidity();
  }

  /**
   * Liga ou desliga a obrigatoriedade do parceiro conforme o tipo escolhido.
   *
   * Limpar o valor ao sair de PARTNER não é detalhe: o backend **recusa** `partnerId` em
   * usuário que não é parceiro, porque o resolver de escopo decide o caminho por esse campo e
   * não pelo papel. Um valor esquecido aqui viraria erro de validação numa tela que não mostra
   * mais o campo — e o usuário não teria como entender o que corrigir.
   */
  verifyPartnerValidators(): void {
    const control = this.userRegisterForm.controls[this.userRegisterProps.partnerId];
    if (!control) return;

    if (this.shouldShowPartnerOptions) {
      control.setValidators([Validators.required]);
      this.loadPartnerOptions();
    } else {
      control.setValue('');
      control.clearValidators();
    }

    control.updateValueAndValidity();
  }

  /**
   * Busca os parceiros para o select, uma vez por abertura da tela.
   *
   * Pede a primeira página com tamanho grande de propósito: o `app-select` filtra em memória,
   * e paginar aqui esconderia parceiro do campo de busca sem nenhum sinal na tela. Se um dia a
   * base passar desse volume, o caminho é busca no servidor, não aumentar o número.
   */
  private loadPartnerOptions(): void {
    if (this.partnerOptionsRequested) return;
    this.partnerOptionsRequested = true;

    this.partnersService.getPartners(1, 500).subscribe({
      next: response => {
        this.partnerOptions = (response?.data || []).map(
          partner =>
            ({
              label: partner.name,
              value: partner.id,
            }) as Option
        );
        this.cdr.detectChanges();
      },
      error: error => {
        // Libera nova tentativa: sem parceiro na lista o cadastro do tipo PARTNER não fecha,
        // então travar o retry deixaria a tela num beco sem saída até recarregar a página.
        this.partnerOptionsRequested = false;
        console.log('loadPartnerOptions error: ', error);
        this.toast.showToastError(
          'Erro ao carregar parceiros',
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
    });
  }

  getUserPermissionsFromService(
    roleId: string,
    from: 'default' | 'admin' | 'tenant' = 'default'
  ): Observable<ApiResponse<PermissionGroup[]> | null> {
    return this.userService.getPermissionsGroups(roleId, null, this.search[from]);
  }

  getUserPermissionsGroups(roleId: string): void {
    this.utilsService.setIsLoading(true);
    this.getUserPermissionsFromService(roleId).subscribe({
      next: response => {
        this.utilsService.setIsLoading(false);
        if (response?.data) {
          this.resetPermissionGroupFormArray();
          this.permissionsGroups = response.data;
          this.reset_permissionsCheckboxes('default');
          this.cdr.detectChanges();
        }
      },
      error: error => {
        this.utilsService.setIsLoading(false);
        console.log('getUserPermissionsGroups error: ', error);
        this.toast.showToastError(
          this.t(this.tk.User.UserErrorLoadingPermissions),
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
    });
  }

  get permissionsGroupsAdminTenantAsArray(): string[] {
    return Object.keys(this.permissionsGroupsAdminTenant);
  }

  get shouldShowTenantOptions(): boolean {
    return (
      (this.selectedUserType?.label || '').toLocaleLowerCase().includes('tenant') ||
      (this.selectedUserType?.label || '').toLocaleLowerCase().includes('company')
    );
  }
  /**
   * Mostra o seletor de parceiro comercial.
   *
   * Sai do tipo **selecionado agora** (`selectedUserType`), e não de `user.roles` como o
   * `isPartnerUserType`: este governa o formulário de criação, onde ainda não existe usuário.
   * São perguntas diferentes — "o que estou criando" e "o que este usuário já é".
   *
   * O parceiro é obrigatório para o tipo PARTNER: sem ele o usuário nasce órfão e entra na
   * plataforma sem enxergar deal nenhum nem empresa nenhuma, sem erro que explique.
   *
   * **Só na criação.** O `UserDto` de leitura não devolve `partnerId` e o `UpdateUserDto` não
   * o aceita, então na edição o campo nasceria vazio e obrigatório: o botão Finalizar ficaria
   * desabilitado para sempre em qualquer usuário parceiro já existente. Trocar o parceiro de
   * um usuário depende de expor o campo nos dois DTOs — enquanto isso não existe, o vínculo é
   * definido no cadastro.
   */
  get shouldShowPartnerOptions(): boolean {
    return (
      !this.userId && (this.selectedUserType?.label || '').toLocaleUpperCase().includes('PARTNER')
    );
  }

  get shouldShowCompaniesOptions(): boolean {
    return (this.selectedUserType?.label || '').toLocaleLowerCase().includes('company');
  }

  /**
   * Indica se o usuário editado é um parceiro comercial.
   *
   * O painel de escopo não é mais funcional para ele: não define nem acrescenta empresa
   * nenhuma daqui. Por isso o painel recebe `partnerMode` e renderiza só um aviso apontando
   * para Gerenciamento › Parceiros › Empresas, onde o vínculo manual passou a viver (do
   * parceiro, não do usuário). O `PUT /users/{id}/company-access` recusa os TRÊS modos
   * (grupo econômico, empresa única e múltiplas empresas) para usuário de parceiro.
   *
   * Sai de `user.roles`, e **não** do `selectedUserType` como os `shouldShow*` daqui de cima:
   * PARTNER fica de propósito fora da lista de tipos atribuíveis (`getUserRoles`), então ao
   * abrir um parceiro o select de Tipo vem vazio — "Selecione" — e um getter baseado nele
   * nunca seria verdadeiro. Medido na tela em 11/09/2026, com o painel aparecendo no modo
   * errado por causa disso.
   */
  get isPartnerUserType(): boolean {
    return (this.user?.roles || []).some(role => (role?.name || '').toUpperCase() === 'PARTNER');
  }

  /**
   * Esconde as preferências de notificação para usuário parceiro (card 3517, pedido do Breno em
   * 19/10/2026): Vencimentos, Risco, Comitê, Mandatos e Comercial são de módulos que o parceiro
   * não acessa, como na tela Preferências dele. Vale na edição (o usuário já é PARTNER) e na
   * criação (o tipo escolhido é Parceiro); sem o card, salvar não grava preferência nenhuma.
   */
  get hidesNotificationPreferences(): boolean {
    return this.isPartnerUserType || this.shouldShowPartnerOptions;
  }

  /**
   * Seção Mercado das preferências (Relatório Semanal de Mercado, card 3617): só para quem tem
   * `MARKETMONITOR_VIEW` nos grupos marcados; parceiro nunca. Sem como saber, fica escondida.
   */
  get showMarketPreferenceSection(): boolean {
    if (this.isPartnerUserType || this.shouldShowPartnerOptions) return false;

    return selectedGroupsGrantMarketMonitorView(
      this.userRegisterForm.getRawValue().permissionsGroupsIds,
      this.permissionsGroups,
      this.permissionsGroupsAdminTenant.tenant,
      this.permissionsGroupsAdminTenant.admin,
      this.user?.permissionsGroups
    );
  }

  get permissionGroupFormArray() {
    return this.userRegisterForm.get(this.userRegisterProps.permissionsGroupsIds) as FormArray;
  }

  get permissionsTenantCountSelected(): number {
    let count = 0;
    count =
      this.userRegisterForm.getRawValue().permissionsGroupsIds.filter((id: string) => {
        return this.permissionsTenantCheckboxes?.map(permission => permission.value).includes(id);
      }).length || 0;

    return count;
  }

  get permissionsAdminCountSelected(): number {
    let count = 0;
    count =
      this.userRegisterForm.getRawValue().permissionsGroupsIds.filter((id: string) => {
        return this.permissionsAdminCheckboxes?.map(permission => permission.value).includes(id);
      }).length || 0;
    return count;
  }

  get permissionsCountSelected(): number {
    let count = 0;
    count =
      this.userRegisterForm.getRawValue().permissionsGroupsIds.filter((id: string) => {
        return this.permissionsCheckboxes?.map(permission => permission.value).includes(id);
      }).length || 0;

    return count;
  }

  resetPermissionGroupFormArray() {
    while (this.permissionGroupFormArray.length !== 0) {
      this.permissionGroupFormArray.removeAt(0);
    }
    this.permissionGroupFormArray.updateValueAndValidity();
  }

  get permissionsCheckboxes(): Checkbox[] {
    if (!this._permissionsCheckboxes) {
      this._permissionsCheckboxes = this.permissionsGroups.map(permissionGroup => {
        let hasId;
        if (this.user && permissionGroup) {
          hasId = this.user.permissionsGroups?.filter(x => x.id == permissionGroup?.id).length == 1;
        }

        const checkbox = {
          label: permissionGroup.name,
          name: permissionGroup.id,
          value: permissionGroup.id,
          isDisabled: false,
          isChecked: hasId,
        } as Checkbox;

        if (hasId) {
          const controlIndex = this.permissionGroupFormArray.controls.findIndex(
            control => control.value === checkbox.value
          );

          if (controlIndex === -1) {
            this.permissionGroupFormArray.controls.push(this.formBuilder.control(checkbox.value));
            this.userRegisterForm.controls[
              this.userRegisterProps.permissionsGroupsIds
            ].updateValueAndValidity();
          }
        }

        return { ...checkbox };
      });
      return this._permissionsCheckboxes;
    }
    return this._permissionsCheckboxes!;
  }

  get permissionsAdminCheckboxes(): Checkbox[] {
    if (!this._permissionsAdminCheckboxes) {
      this._permissionsAdminCheckboxes = (this.permissionsGroupsAdminTenant?.admin || []).map(
        permissionGroup => {
          let hasId;
          if (this.user && permissionGroup) {
            hasId =
              this.user.permissionsGroups?.filter(x => x.id == permissionGroup?.id).length == 1;
          }
          const checkbox = {
            label: permissionGroup.name,
            name: permissionGroup.id,
            value: permissionGroup.id,
            isDisabled: false,
            isChecked: hasId,
          } as Checkbox;

          if (hasId) {
            const controlIndex = this.permissionGroupFormArray.controls.findIndex(
              control => control.value === checkbox.value
            );

            if (controlIndex === -1) {
              this.permissionGroupFormArray.controls.push(this.formBuilder.control(checkbox.value));
              this.userRegisterForm.controls[
                this.userRegisterProps.permissionsGroupsIds
              ].updateValueAndValidity();
            }
          }

          return { ...checkbox };
        }
      );
      return this._permissionsAdminCheckboxes;
    }
    return this._permissionsAdminCheckboxes!;
  }

  get permissionsTenantCheckboxes(): Checkbox[] {
    if (!this._permissionsTenantCheckboxes) {
      this._permissionsTenantCheckboxes = (this.permissionsGroupsAdminTenant?.tenant || []).map(
        permissionGroup => {
          let hasId;
          if (this.user && permissionGroup) {
            hasId =
              this.user.permissionsGroups?.filter(x => x.id == permissionGroup?.id).length == 1;
          }
          const checkbox = {
            label: permissionGroup.name,
            name: permissionGroup.id,
            value: permissionGroup.id,
            isDisabled: false,
            isChecked: hasId,
          } as Checkbox;

          if (hasId) {
            // Verifica se já existe um controle com o mesmo valor antes de adicionar
            const controlIndex = this.permissionGroupFormArray.controls.findIndex(
              control => control.value === checkbox.value
            );

            if (controlIndex === -1) {
              this.permissionGroupFormArray.controls.push(this.formBuilder.control(checkbox.value));
              this.userRegisterForm.controls[
                this.userRegisterProps.permissionsGroupsIds
              ].updateValueAndValidity();
            }
          }

          return { ...checkbox };
        }
      );
      return this._permissionsTenantCheckboxes;
    }
    return this._permissionsTenantCheckboxes!;
  }

  handleCheckedChange(
    checkbox: Checkbox,
    from: 'default' | 'admin' | 'tenant' | 'sendOnboardingEmail' | 'isPublicGlobalBackofficeUser'
  ): void {
    const isChecked = checkbox.isChecked;
    let checkboxIndex = -1;

    switch (from) {
      case 'admin':
        checkboxIndex = this.permissionsAdminCheckboxes.findIndex(
          permission => permission.value === checkbox.value
        );
        if (checkboxIndex !== -1) {
          this.permissionsAdminCheckboxes[checkboxIndex].isChecked = isChecked;
        }
        break;
      case 'tenant':
        checkboxIndex = this.permissionsTenantCheckboxes.findIndex(
          permission => permission.value === checkbox.value
        );
        if (checkboxIndex !== -1) {
          this.permissionsTenantCheckboxes[checkboxIndex].isChecked = isChecked;
        }
        break;
      case 'sendOnboardingEmail':
        this.sendOnboardingEmailCheckbox.isChecked = isChecked;
        break;
      case 'isPublicGlobalBackofficeUser':
        this.isPublicGlobalBackofficeUserCheckbox.isChecked = isChecked;
        this.userRegisterForm.controls[
          this.userRegisterProps.isPublicGlobalBackofficeUser
        ].setValue(isChecked);
        break;
      default:
        checkboxIndex = this.permissionsCheckboxes.findIndex(
          permission => permission.value === checkbox.value
        );
        if (checkboxIndex !== -1) {
          this.permissionsCheckboxes[checkboxIndex].isChecked = isChecked;
        }
    }

    if (from !== 'sendOnboardingEmail' && from !== 'isPublicGlobalBackofficeUser') {
      const controlIndex = this.permissionGroupFormArray.controls.findIndex(
        control => control.value === checkbox.value
      );

      if (checkbox.isChecked) {
        if (controlIndex === -1) {
          this.permissionGroupFormArray.controls.push(this.formBuilder.control(checkbox.value));
        }
        this.userRegisterForm.controls[
          this.userRegisterProps.permissionsGroupsIds
        ].updateValueAndValidity();
      } else {
        this.permissionGroupFormArray.removeAt(controlIndex);
        this.userRegisterForm.controls[
          this.userRegisterProps.permissionsGroupsIds
        ].updateValueAndValidity();
      }
    }
  }

  reset_permissionsCheckboxes(form: 'default' | 'admin' | 'tenant' | 'all' = 'all') {
    switch (form) {
      case 'admin':
        this._permissionsAdminCheckboxes = null;
        break;
      case 'tenant':
        this._permissionsTenantCheckboxes = null;
        break;
      case 'default':
        this._permissionsCheckboxes = null;
        break;
      case 'all':
        this._permissionsAdminCheckboxes = null;
        this._permissionsTenantCheckboxes = null;
        this._permissionsCheckboxes = null;
        break;
      default:
        break;
    }
  }

  handleCheckAllPermissions(checkbox: Checkbox, from: 'admin' | 'tenant' | 'default'): void {
    const isChecked = checkbox.isChecked;
    this.checkAllPermissions.isChecked = isChecked;

    let checkboxes = this.permissionsCheckboxes;

    switch (from) {
      case 'admin':
        checkboxes = this.permissionsAdminCheckboxes;
        this.checkAllAdminPermissions.isChecked = isChecked;
        this.reset_permissionsCheckboxes(from);
        this.verifyCheckboxPermissionGroupFormArray(checkboxes, isChecked);
        break;
      case 'tenant':
        checkboxes = this.permissionsTenantCheckboxes;
        this.checkAllTenantPermissions.isChecked = isChecked;
        this.reset_permissionsCheckboxes(from);
        this.verifyCheckboxPermissionGroupFormArray(checkboxes, isChecked);
        break;
      default:
        checkboxes = this.permissionsCheckboxes;
        this.checkAllPermissions.isChecked = isChecked;

        this.permissionsCheckboxes.forEach(permissionGroup => {
          permissionGroup.isChecked = isChecked;
        });

        this.verifyCheckboxPermissionGroupFormArray(checkboxes, isChecked);
        this.reset_permissionsCheckboxes(from);
        break;
    }
  }

  verifyCheckboxPermissionGroupFormArray(checkboxes: Checkbox[] = [], isChecked: boolean = false) {
    checkboxes.forEach((permissionGroup: Checkbox) => {
      const controlIndex = this.permissionGroupFormArray.controls.findIndex(
        control => control.value === permissionGroup.value
      );

      if (isChecked) {
        if (controlIndex === -1) {
          this.permissionGroupFormArray.controls.push(
            this.formBuilder.control(permissionGroup.value)
          );
        }
      } else {
        this.permissionGroupFormArray.removeAt(controlIndex);
      }
    });
  }

  handleSearch(search: string = '', from: 'default' | 'tenant' | 'admin'): void {
    if (!from) return;
    this.search[from] = search;
    const roles = this.userRegisterForm.getRawValue()?.roles || [];

    if (roles.length > 1) {
      this.handleMultiRolesRequest(roles);
    } else {
      if (!roles.length) return;
      this.getUserPermissionsGroups(roles[0]);
    }
  }

  registerUser(): void {
    if (this.userRegisterForm.invalid) {
      this.userRegisterForm.markAllAsTouched();
      return;
    }

    const { tenantId, partnerId, ...userRegisterRequest } = this.userRegisterForm.getRawValue();

    delete userRegisterRequest.search;
    delete userRegisterRequest.companyId;
    let userData = this.shouldShowTenantOptions
      ? { ...userRegisterRequest, tenantId }
      : userRegisterRequest;

    // `partnerId` só entra quando o tipo é PARTNER. Fora daí o controle guarda string vazia,
    // que o backend tenta desserializar em `Guid?` e devolve 400 antes de qualquer validação —
    // um erro sem mensagem útil numa tela que nem mostra o campo.
    if (this.shouldShowPartnerOptions && partnerId) {
      userData = { ...userData, partnerId };
    }

    if (!this.userId && this.selectedUserType?.label === 'COMPANY') {
      userData = { ...userData, sendOnboardingEmail: this.sendOnboardingEmailCheckbox.isChecked };
    }
    userData = {
      ...userData,
      isPublicGlobalBackofficeUser: this.isPublicGlobalBackofficeUserCheckbox.isChecked,
    };

    if (userData.phoneNumber === '55') userData.phoneNumber = null;

    if (this.userId) {
      this.editUser(userData);
    } else {
      this.postUser(userData);
    }
  }

  postUser(userData: UserRegisterRequest): void {
    this.utilsService.setIsLoading(true);
    this.userService.postUser(userData).subscribe({
      next: response => {
        // `postUser` está tipado como `UserRegisterRequest`, mas a API devolve o usuário
        // criado (com `id`). O cast local mantém o acesso ao id sem espalhar `any`.
        const createdUserId = (response as { id?: string } | null)?.id;

        // Salvar preferências de notificação se houver alguma selecionada (parceiro não tem o card)
        const notificationPreferences = this.buildNotificationPreferencesPayloadFromForm();
        const hasPreferences =
          !!notificationPreferences &&
          (notificationPreferences.expirationAlertPreferences.length > 0 ||
            notificationPreferences.hedgePositionReportPreference.enabledDays.length > 0 ||
            notificationPreferences.committeeAlertPreferences.length > 0 ||
            notificationPreferences.mandateAlertPreferences.length > 0 ||
            !!notificationPreferences.marketWeeklyReportPreference?.emailEnabled);

        // Usuário foi criado: falha nas preferências ou na assinatura não interrompe o fluxo.
        const preferences$ =
          hasPreferences && createdUserId
            ? this.notificationPreferenceService
                .updateUserPreferences(createdUserId, notificationPreferences!)
                .pipe(
                  catchError(error => {
                    this.warnNotificationPreferencesNotSaved(error);
                    return of(null);
                  })
                )
            : of(null);
        preferences$
          .pipe(switchMap(() => this.saveEmailSignature(createdUserId)))
          .subscribe(() => this.persistAccessScopeThenFinish(createdUserId));
      },
      error: error => {
        this.utilsService.setIsLoading(false);
        if (error instanceof ErrorResponse) {
          this.formErrorHandler.applyFormErrors(this.userRegisterForm, error);
        } else {
          console.log('postUser error: ', error);
        }
        this.toast.showToastError(
          this.t(this.tk.User.UserErrorCreatingUser),
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
    });
  }

  /**
   * Persiste o escopo de empresas escolhido durante a criação e encerra o fluxo.
   *
   * Na criação o painel de escopo roda como seletor puro (`autoSave = false`), porque o
   * `PUT /users/{id}/company-access` precisa de um usuário já existente. Este é o ponto
   * em que o `userId` finalmente existe. Sem escopo escolhido (ou sem id na resposta), o
   * cadastro segue como antes.
   *
   * Uma falha aqui não invalida o usuário recém-criado: avisamos que o escopo não foi
   * salvo e seguimos, para o admin ajustar pela edição.
   */
  private persistAccessScopeThenFinish(createdUserId?: string): void {
    const shouldPersistScope = !!createdUserId && !!this.accessPanel?.hasScopeSelection();

    if (!shouldPersistScope) {
      this.finishUserCreation();
      return;
    }

    this.economicGroupsService
      .setUserAccess(createdUserId!, this.accessPanel!.buildAccessPayload())
      .subscribe({
        next: () => this.finishUserCreation(),
        error: error => {
          console.error('Error saving user company access scope:', error);
          this.utilsService.setIsLoading(false);
          this.toast.showToastError(
            this.t(this.tk.User.UserCreatedSuccessfully),
            'Usuário criado, mas o escopo de empresas não pôde ser salvo. Defina o escopo pela edição do usuário.'
          );
          this.navigateAfterUserCreation();
        },
      });
  }

  /** Fecha o fluxo de criação com sucesso: encerra o loading, avisa e navega. */
  private finishUserCreation(): void {
    this.utilsService.setIsLoading(false);
    this.toast.showToastSuccess(this.t(this.tk.User.UserCreatedSuccessfully));
    this.navigateAfterUserCreation();
  }

  /** Usuário COMPANY volta para a lista de empresas; os demais, para a lista de usuários. */
  private navigateAfterUserCreation(): void {
    if (this.shouldShowCompaniesOptions) {
      this.router.navigate(['dashboard/company/list']);
    } else {
      this.router.navigate(['dashboard/user/list']);
    }
  }

  editUser(userData: UserRegisterRequest): void {
    this.utilsService.setIsLoading(true);
    this.userService.putUser(userData, this.userId).subscribe({
      next: () => {
        // Salvar preferências de notificação (usuário parceiro não tem o card: pula direto)
        const notificationPreferences = this.buildNotificationPreferencesPayloadFromForm();
        const preferences$ = notificationPreferences
          ? this.notificationPreferenceService.updateUserPreferences(
              this.userId,
              notificationPreferences
            )
          : of(null);
        preferences$
          .pipe(
            // Usuário foi atualizado, mas preferências falharam - ainda assim mostrar sucesso
            catchError(error => {
              this.warnNotificationPreferencesNotSaved(error);
              return of(null);
            }),
            switchMap(() => this.saveEmailSignature(this.userId))
          )
          .subscribe(() => {
            this.utilsService.setIsLoading(false);
            this.toast.showToastSuccess(this.t(this.tk.User.UserUpdatedSuccessfully));
            this.router.navigate(['dashboard/user/list']);
          });
      },
      error: error => {
        this.utilsService.setIsLoading(false);
        if (error instanceof ErrorResponse) {
          this.formErrorHandler.applyFormErrors(this.userRegisterForm, error);
        } else {
          console.log('putUser error: ', error);
        }
        this.toast.showToastError(
          this.t(this.tk.User.UserErrorUpdatingUser),
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
    });
  }

  /**
   * Preferências recusadas pelo backend (ex.: ligar o Relatório Semanal de Mercado sem acesso ao
   * Monitor, card 3617): o usuário já foi salvo, então avisa com a mensagem do backend e segue.
   */
  private warnNotificationPreferencesNotSaved(error: unknown): void {
    this.toast.showToastWarn(
      `Usuário salvo, mas as preferências de notificação não foram gravadas: ${describeBackendError(error)}`
    );
  }

  /** Grava a assinatura de e-mail (card 3456); falha vira aviso e o fluxo do usuário segue. */
  private saveEmailSignature(userId?: string): Observable<unknown> {
    if (!userId || !this.emailSignatureForm) return of(null);
    return this.emailSignatureForm.save(userId).pipe(
      catchError(error => {
        this.toast.showToastWarn(
          `Usuário salvo, mas a assinatura de e-mail não foi gravada: ${describeBackendError(error)}`
        );
        return of(null);
      })
    );
  }

  private applyNotificationPreferencesToForm(preferences: unknown): void {
    if (this.notificationPreferencesForm) {
      this.notificationPreferencesForm.applyNotificationPreferences(preferences);
      return;
    }

    this.pendingNotificationPreferences = preferences;
  }

  /** Nulo quando o card não existe (usuário parceiro): não há preferência a gravar. */
  private buildNotificationPreferencesPayloadFromForm(): UpdateUserNotificationPreference | null {
    return this.notificationPreferencesForm?.buildNotificationPreferencesPayload() ?? null;
  }

  loadUserNotificationPreferences(userId: string): void {
    this.notificationPreferenceService.getUserPreferences(userId).subscribe({
      next: response => {
        // VaultApiService pode retornar com ou sem wrapper 'data'
        const preferences: any = (response?.data || response) ?? {};
        this.applyNotificationPreferencesToForm(preferences);
      },
      error: () => {
        // Preferências são opcionais, erro não bloqueia o fluxo
      },
    });
  }
}
